import { createStore } from "vuex"

import countryService from "@/services/country"
import partnerService from "@/services/partner"
import qualityService from "@/services/quality"
import type { Country } from "@/types/country.type"
import type { Partner } from "@/types/partner.type"
import type { QualityProperty } from "@/types/quality.type"
import { reportSentry } from "@/utils/sentry"

const KEY_PARTNER_TOKEN = "swatchon-partner-token"
const KEY_PARTNER = "swatchon-partner"
// const KEY_COUNTRIES = "swatchon-countries"

function readCachedPartner(): Partner | null {
  try {
    const raw = localStorage.getItem(KEY_PARTNER)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

function writeCachedPartner(partner: Partner) {
  try {
    localStorage.setItem(KEY_PARTNER, JSON.stringify(partner))
  } catch (_) {}
}

function removeCachedPartner() {
  try {
    localStorage.removeItem(KEY_PARTNER)
  } catch (_) {}
}

// function readCachedCountries() {
//   try {
//     const raw = localStorage.getItem(KEY_COUNTRIES)
//     if (!raw) return null
//     const parsed = JSON.parse(raw)
//     if (!Array.isArray(parsed)) return null
//     return parsed
//   } catch (_) {
//     return null
//   }
// }

// function writeCachedCountries(countries) {
//   try {
//     localStorage.setItem(KEY_COUNTRIES, JSON.stringify(countries))
//   } catch (_) {}
// }

interface State {
  // partner
  token: string | null
  partner: Partner | null
  countries: Country[]
  properties: QualityProperty | null
}

// Create a new store instance.
export default createStore<State>({
  state: {
    // partner
    token: null,
    partner: null,

    // countries
    countries: [],

    // quality
    properties: null,
  },
  mutations: {
    // partner
    saveToken(state, token) {
      state.token = token
      localStorage.setItem(KEY_PARTNER_TOKEN, token)
    },
    setPartner(state, partner) {
      state.partner = partner || null
      if (partner) writeCachedPartner(partner)
    },
    clearAuth(state) {
      state.token = null
      localStorage.removeItem(KEY_PARTNER_TOKEN)
      state.partner = null
      removeCachedPartner()
    },

    // countries
    setCountries(state, countries) {
      state.countries = countries || []
    },

    // quality
    setProperties(state, properties) {
      state.properties = properties || null
    },
  },
  getters: {
    // customer
    token: (state) => state.token,
    partner: (state) => state.partner,

    // countries
    countries: (state) => state.countries,

    // properties
    properties: (state) => state.properties,
  },
  actions: {
    /**
     * Load partner with localStorage cache.
     * Order: in-memory -> localStorage -> API (/partners/me)
     * Usage: await store.dispatch("loadPartner") or force: true to refresh
     */
    async loadPartner({ state, commit }, { force = false } = {}): Promise<Partner | null> {
      // 0) If no token, wipe any stale partner data and stop
      const token = typeof state.token === "string" ? state.token.trim() : ""
      if (!token) {
        // clearAuth already removes cached partner via removeCachedPartner()
        commit("clearAuth")
        return null
      }

      // 1) In-memory
      if (!force && state.partner) return state.partner

      // 2) Local cache (only when token exists)
      if (!force) {
        const cached = readCachedPartner()
        if (cached) {
          commit("setPartner", cached)
          return cached
        }
      }

      // 3) API
      try {
        const data = await partnerService.me()
        commit("setPartner", data)
        return data
      } catch (error) {
        console.error(error)
        reportSentry(error)
        return null
      }
    },

    async loadCountries({ commit }) {
      const countries = await countryService.countries()
      commit("setCountries", countries)

      return countries
    },

    async loadProperties({ commit }) {
      const properties = await qualityService.properties()
      commit("setProperties", properties)

      return properties
    },
  },
})
